import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { IVan } from '../../../../../app/entities/Van'
import { AuditContext } from '../../../global/context/audit-context/AuditContext'
import { useAuditConfig } from '../../../global/context/audit-config-context/AuditConfigContext'
import {
  IAuditDefect,
  IAuditOption,
  IAuditPrompt,
  IAuditState,
  STATUS,
} from '../../../global/types/audit'
import { fetchData } from '../../../global/utils/fetch'
import PassFailButton from '../common/pass-fail-button/PassFailButton'
import DefectsDisplay from '../common/defects-display/DefectsDisplay'
import ImagesDisplay from '../common/images-display/ImagesDisplay'
import RadioButtonsDisplay from '../common/radio-buttons-display/RadioButtonsDisplay'
import TextBoxDisplay from '../common/text-box-display/TextBoxDisplay'
import { updateVan } from './api'
import styles from './styles.module.css'
import { initialAuditNote, AI_DECISION } from '../../../global/constants/audit'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { useExampleImages } from '../common/hooks/useExampleImages'

const INSTALLATION_AUDIT_STATUSES: string[] = [
  STATUS.COMPLETED_INSTALLATION,
  STATUS.ON_SITE_CANCELLATION,
]

async function updateVanData(
  promptKey: string,
  value: string,
  van: IVan | undefined,
  vanDataMappings: Record<string, keyof IVan>,
) {
  if (!van) return

  // Check if this prompt key should trigger a van update
  const vanProperty = vanDataMappings[promptKey]
  if (!vanProperty) return

  // Type-safe dynamic property update
  // Note: We're updating with string values, but the backend handles type conversion
  if (vanProperty in van) {
    ;(van as Record<keyof IVan, any>)[vanProperty] = value
  }
  await fetchData(updateVan(van))
}

interface IModalContentProps {
  section: string
  handleNextSection: (newAuditState: IAuditState | null) => void
  handlePreviousSection: () => void
  handleClose: (
    newProgress: string,
    newAuditState: IAuditState | null,
  ) => Promise<void>
  setIsOrderDetailsModalOpen: Dispatch<SetStateAction<boolean>>
}

export default function ModalContent({
  section,
  handleNextSection,
  handlePreviousSection,
  handleClose,
  setIsOrderDetailsModalOpen,
}: IModalContentProps) {
  const {
    auditSectionData,
    technicianImages,
    auditState,
    sections,
    auditData,
    auditStatus,
  } = useContext(AuditContext)

  // Fetch example images dynamically for current section
  const { exampleImages } = useExampleImages({
    auditType: auditStatus,
    sectionKey: section,
  })

  // Get VAN_DATA_MAPPINGS for Weekly DVIR van updates
  const weeklyDvirConfig = useAuditConfig(STATUS.WEEKLY_DVIR)
  const vanDataMappings: Record<string, keyof IVan> =
    (weeklyDvirConfig.VAN_DATA_MAPPINGS as Record<string, keyof IVan>) || {}

  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const currentInstallationSection = auditSectionData[section]

  const { title, imageTypes, question, answer, defects, options, prompt } =
    currentInstallationSection

  const [defectsState, setDefectsState] = useState<IAuditDefect>({})
  const [optionsState, setOptionsState] = useState<IAuditOption>({})
  const [promptState, setPromptState] = useState<IAuditPrompt>({})
  const [optionalNoteState, setOptionalNoteState] =
    useState<IAuditPrompt>(initialAuditNote)
  const [isPassing, setIsPassing] = useState<boolean>(true)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const showOrderDetailsButton = INSTALLATION_AUDIT_STATUSES.includes(
    auditStatus || '',
  )

  const sectionIndex = Object.values(sections).indexOf(section) + 1
  const totalSections = Object.keys(sections).length - 1
  const progressString = `${sectionIndex} of ${totalSections}`

  function handleBackClick() {
    setToastStatus({
      ...toastStatus,
      isOpen: true,
      message: `Your forward progress has been lost.`,
      severity: 'error',
    })
    handlePreviousSection()
  }

  function handlePassFailClick() {
    const selectedDefects = Object.fromEntries(
      Object.entries(defectsState).filter(([_, value]) => value),
    )

    const selectedOption = Object.fromEntries(
      Object.entries(optionsState)
        .filter(([_, value]) => {
          const isSelectedOption = value.selection
          const isFailingSelection = value.selection !== value.answerKey
          return isSelectedOption && isFailingSelection
        })
        .map(([key, value]) => [key, value.selection]),
    )

    const requiredPromptValues = Object.fromEntries(
      Object.entries(promptState).map(([key, prompt]) => {
        updateVanData(key, prompt.value, auditData?.dvir?.van, vanDataMappings)
        return [key, prompt.value]
      }),
    )
    const optionalPromptValues = Object.entries(optionalNoteState)
      .map(([_, prompt]) => prompt.value)
      .join(' ')

    const newResponses = {
      ...selectedDefects,
      ...selectedOption,
      ...requiredPromptValues,
    }
    const shouldUpdateState = Object.keys(newResponses).length > 0
    const needsCoaching =
      Object.keys({ ...selectedDefects, ...selectedOption }).length > 0
    let newAuditState = auditState
    if (shouldUpdateState) {
      newAuditState = {
        ...auditState,
        [section]: {
          question,
          response: newResponses,
          coaching: {
            coached: false,
            needsCoaching,
            notes: optionalPromptValues
              ? '(From auditor: ' + optionalPromptValues + ')'
              : '',
          },
          // Preserve existing AI review if it exists
          ...(auditState?.[section]?.aiReview && {
            aiReview: auditState[section].aiReview,
          }),
        },
      }
    }
    handleNextSection(newAuditState)
    setOptionalNoteState(initialAuditNote)
    setCurrentImageIndex(0)
  }

  useEffect(() => {
    setDefectsState(defects || {})
    setOptionsState(options || {})
    setPromptState(prompt || {})
  }, [defects, options, prompt])

  useEffect(() => {
    const defectsSelected = Object.values(defectsState).some((defect) => defect)
    const optionsSelected = Object.values(optionsState).some(
      (option) => option.selection,
    )
    const isPassingSelection = Object.values(optionsState).every(
      (option) => option.selection === option.answerKey,
    )
    const requiredPromptTyped = Object.values(promptState).every(
      (prompt) => prompt.value.length > 0,
    )
    const optionalPromptTyped = Object.values(optionalNoteState).some(
      (prompt) => prompt.value.length > 0,
    )
    if (defects) {
      if (prompt) {
        setIsDisabled(!defectsSelected && !requiredPromptTyped)
      } else {
        setIsDisabled(false)
      }
      if (optionalPromptTyped) {
        setIsDisabled(!defectsSelected)
      }
      setIsPassing(!defectsSelected)
    } else if (options) {
      setIsDisabled(!optionsSelected)
      if (optionsSelected) setIsPassing(isPassingSelection)
    }
  }, [optionsState, defectsState, promptState, optionalNoteState])

  return (
    <>
      <div className={styles.pageTitle}>
        <div>{title}</div>
        {showOrderDetailsButton ? (
          <SecondaryButton
            buttonName='View Order Details'
            onClick={() => setIsOrderDetailsModalOpen(true)}
            style={{ minWidth: '150px' }}
          />
        ) : (
          <></>
        )}
      </div>
      <div className={styles.imagesContainer}>
        <ImagesDisplay
          imageTypes={imageTypes}
          technicianImages={technicianImages}
          exampleImages={exampleImages}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
        />
      </div>
      <div className={styles.inputContainer}>
        <div className={styles.questionContainer}>
          {question}
          <span
            style={{
              fontWeight: 'bold',
              paddingTop: '10px',
              paddingBottom: '20px',
            }}
          >
            {answer}
          </span>
        </div>
        {auditState?.[section]?.aiReview && (() => {
          const aiReview = auditState[section].aiReview!
          const isFailed = aiReview.decision === AI_DECISION.fail
          return (
            <div
              style={{
                backgroundColor: isFailed ? '#FEE2E2' : '#DCFCE7',
                borderLeft: isFailed ? '4px solid #DC2626' : '4px solid #16A34A',
                padding: '16px',
                marginBottom: '20px',
                borderRadius: '4px',
              }}
            >
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                color: isFailed ? '#991B1B' : '#166534'
              }}>
                {isFailed ? 'AI Flagged This Section' : 'AI Passed This Section'}
              </div>
              <div style={{
                color: isFailed ? '#7F1D1D' : '#14532D',
                fontSize: '14px'
              }}>
                {aiReview.reasoning}
              </div>
            </div>
          )
        })()}
        {prompt && (
          <TextBoxDisplay
            promptState={promptState}
            setPromptState={setPromptState}
          />
        )}
        {defects && (
          <DefectsDisplay
            defectsState={defectsState}
            setDefectsState={setDefectsState}
          />
        )}
        {options && (
          <RadioButtonsDisplay
            optionsState={optionsState}
            setOptionsState={setOptionsState}
          />
        )}
        <TextBoxDisplay
          promptState={optionalNoteState}
          setPromptState={setOptionalNoteState}
        />
      </div>
      <div className={styles.footerContainer}>
        <div>
          <button
            className={styles.footerButton}
            onClick={() => handleBackClick()}
          >
            Back
          </button>
        </div>
        <PassFailButton
          pass={isPassing}
          onClick={handlePassFailClick}
          disabled={isDisabled}
          passLabel={prompt ? 'next' : 'pass'}
        />
        <div>
          <button
            className={styles.footerButton}
            onClick={() => handleClose(section, auditState)}
          >
            Save and Exit
          </button>
          {progressString}
        </div>
      </div>
    </>
  )
}
